import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, race, timer } from 'rxjs';
import { first, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { PatientCardStateService } from '@modules/patient-card/services/patient-card-state.service';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';

/** Time in ms to wait for patient-card MFE to become ready before falling back to PatientApp */
const PATIENT_CARD_READY_TIMEOUT_MS = 60_000;

/** Inputs used to resolve the patient display mode */
interface DisplayModeInputs {
	hasPatientCard: boolean;
	hasUnifiedPatients: boolean;
	hasNewFlowRx: boolean;
	isPatientReadOnly: boolean;
	preferOldRxMode: boolean;
}

/**
 * Centralized service that determines which patient display mode to use.
 *
 * Priority (highest to lowest):
 * 1. PatientCard — if PatientCardEnabled SWO is on, online, and the user has not toggled back
 * to classic RX mode.
 * Patient linkage inside the MFE (UnifiedPatientsEnabled SWO) is handled by patient-card itself.
 * 2. UnifiedPatients — if UnifiedPatientsEnabled SWO is on, online, and either NewFlowRx is off
 * or the user has toggled back to classic RX mode
 * 3. PatientApp — legacy iframe fallback (e.g. NewFlowRx on but PatientCardEnabled off)
 *
 * ReadOnly override: when the patient section is in read-only state (e.g., submitted RX),
 * the mode falls back to PatientApp so the classic rx-patient-read-only component is rendered.
 *
 * Classic mode override: when FallbackToOldRxIsAvailable SWO is on and user has toggled
 * back to classic mode (preferOldRxMode), PatientCard is not used.
 *
 * Fallback: if PatientCard is selected but the MFE does not become ready within 60 seconds
 * or reports an error before becoming ready, falls back to PatientApp.
 */
@Injectable({ providedIn: 'root' })
export class PatientDisplayModeService {
	/** Compute patient component to render */
	readonly displayMode$: Observable<PatientDisplayMode> = combineLatest({
		hasPatientCard: this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.PatientCardEnabled),
		hasUnifiedPatients: this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.UnifiedPatientsEnabled),
		hasNewFlowRx: this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.NewFlowRx),
		isPatientReadOnly: this.shellQuery.getReadOnlyState('patient'),
		preferOldRxMode: this.intuitiveFlowService.preferOldRxMode$
	}).pipe(
		map(inputs => this.determineMode(inputs)),
		switchMap(mode => this.fallbackDisplayMode$(mode)),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly patientCardStateService: PatientCardStateService,
		private readonly intuitiveFlowService: IntuitiveFlowService
	) {}

	/** Determines intended mode from company configuration, connectivity, read-only state, and user preference */
	private determineMode({
		hasPatientCard,
		hasUnifiedPatients,
		hasNewFlowRx,
		isPatientReadOnly,
		preferOldRxMode
	}: DisplayModeInputs): PatientDisplayMode {
		if (this.hostPlatformService.isOfflineMode() || isPatientReadOnly) {
			return PatientDisplayMode.PatientApp;
		}

		if (hasPatientCard && !preferOldRxMode) {
			return PatientDisplayMode.PatientCard;
		}

		if (hasUnifiedPatients && (!hasNewFlowRx || preferOldRxMode)) {
			return PatientDisplayMode.UnifiedPatients;
		}

		return PatientDisplayMode.PatientApp;
	}

	/** If PatientCard — races readiness vs error/timeout; otherwise passes mode through */
	private fallbackDisplayMode$(mode: PatientDisplayMode): Observable<PatientDisplayMode> {
		if (mode !== PatientDisplayMode.PatientCard) {
			return of(mode);
		}

		const fallback$ = race(
			this.patientCardStateService.isPatientCardReady$.pipe(
				first(isReady => isReady),
				map(() => PatientDisplayMode.PatientCard)
			),
			this.patientCardStateService.isPatientCardError$.pipe(
				first(isError => isError),
				map(() => PatientDisplayMode.PatientApp)
			),
			timer(PATIENT_CARD_READY_TIMEOUT_MS).pipe(map(() => PatientDisplayMode.PatientApp))
		);

		return fallback$.pipe(startWith(mode));
	}
}
